// @ts-check
/**
 * @file errorMessage.js
 * @location src/integrations/supabase/errorMessage.js
 * @role Supabase 인증/RPC 에러를 한국어 메시지로 변환 — 로그인·가입·리셋·초대 등 인증 화면 공용.
 *
 *  Supabase Auth 와 PostgreSQL RPC(raise exception) 는 영어 메시지를 던진다
 *  (예: "Invalid login credentials" / "invalid code"). 한국 전용 제품이라 화면에는
 *  절대 영어가 나가면 안 되므로, error.code 우선 + message 부분일치 폴백으로 한국어로 치환하고,
 *  매칭 안 되는 미지의 에러는 영어 원문 대신 한국어 fallback 을 노출한다.
 */

/** Supabase Auth code 기반 매핑 (supabase-js v2 AuthApiError.code) */
const BY_CODE = {
  invalid_credentials: '이메일 주소 또는 비밀번호가 올바르지 않습니다.',
  email_not_confirmed: '이메일 주소 확인이 완료되지 않았습니다. 확인 메일의 링크를 열어 주세요.',
  email_exists: '이 이메일 주소는 이미 등록되어 있습니다.',
  user_already_exists: '이 이메일 주소는 이미 등록되어 있습니다.',
  weak_password: '비밀번호는 8자 이상으로 설정해 주세요.',
  same_password: '현재와 다른 비밀번호를 설정해 주세요.',
  over_request_rate_limit: '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.',
  over_email_send_rate_limit: '메일 발송이 집중되고 있습니다. 잠시 후 다시 시도해 주세요.',
  validation_failed: '이메일 주소 형식이 올바르지 않습니다.',
  user_not_found: '해당 계정을 찾을 수 없습니다.',
  session_not_found: '세션이 만료되었습니다. 다시 로그인해 주세요.',
  refresh_token_not_found: '세션이 만료되었습니다. 다시 로그인해 주세요.',
};

/**
 * message 부분일치 폴백 — code 없는 구버전 Auth + RPC(raise exception) 영어 문구.
 * @type {[RegExp, string][]}
 */
const BY_MESSAGE = [
  // --- Supabase Auth (영어 메시지) ---
  [/invalid login credentials/i, '이메일 주소 또는 비밀번호가 올바르지 않습니다.'],
  [/email not confirmed/i, '이메일 주소 확인이 완료되지 않았습니다. 확인 메일의 링크를 열어 주세요.'],
  [/already registered|already been registered|user already exists/i, '이 이메일 주소는 이미 등록되어 있습니다.'],
  [/password should be at least|weak password/i, '비밀번호는 8자 이상으로 설정해 주세요.'],
  [/new password should be different|different from the old/i, '현재와 다른 비밀번호를 설정해 주세요.'],
  [/email rate limit|over_email_send/i, '메일 발송이 집중되고 있습니다. 잠시 후 다시 시도해 주세요.'],
  [/rate limit|too many requests/i, '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.'],
  [/unable to validate email|invalid email|invalid format/i, '이메일 주소 형식이 올바르지 않습니다.'],
  [/failed to fetch|network|networkerror|timeout/i, '통신 오류가 발생했습니다. 연결을 확인해 주세요.'],
  // --- RPC raise exception (영어 문구) ---
  [/not authenticated/i, '인증이 만료되었습니다. 다시 로그인해 주세요.'],
  [/expired code/i, '초대 코드가 만료되었습니다. 매니저에게 재발급을 요청해 주세요.'],
  [/invalid code/i, '초대 코드가 올바르지 않습니다. 다시 확인해 주세요.'],
  [/already a member/i, '이미 팀에 참여하고 있습니다.'],
  [/name required|title required/i, '필수 항목을 입력해 주세요.'],
  // 인원 게이팅(rpc_create_invite) — 플랜 멤버 상한 도달
  [/member_cap_reached/i, '멤버 수가 플랜 상한에 도달했습니다. 상위 플랜으로 업그레이드해 주세요.'],
  // 매니저 양도(rpc_transfer_manager)
  [/only manager can transfer/i, '매니저만 양도할 수 있습니다.'],
  [/cannot transfer to self/i, '본인에게는 양도할 수 없습니다.'],
  [/already a manager/i, '대상은 이미 매니저입니다.'],
  [/different team/i, '대상이 같은 팀에 소속되어 있지 않습니다.'],
  [/cannot delete (self|owner)/i, '이 작업은 실행할 수 없습니다.'],
  [/forbidden/i, '이 작업을 수행할 권한이 없습니다.'],
  [/not found/i, '대상을 찾을 수 없습니다.'],
];

/**
 * Supabase 인증/RPC 에러 → 한국어 메시지.
 * @param {any} error  Supabase AuthError / PostgREST error / 임의 throw 값
 * @param {string} [fallback]  매칭 안 될 때 기본 한국어 메시지 (영어 원문 노출 방지)
 * @returns {string}
 */
export function localizeAuthError(error, fallback = '오류가 발생했습니다. 잠시 후 다시 시도해 주세요.') {
  if (!error) return fallback;
  const code = error.code ?? error.error_code;
  if (code && BY_CODE[code]) return BY_CODE[code];
  const msg = typeof error === 'string' ? error : (error.message ?? '');
  for (const [re, jp] of BY_MESSAGE) {
    if (re.test(msg)) return jp;
  }
  return fallback;
}
